import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { listHospitals, createEmergency, fileUrl } from "../lib/api";
import { Hospital as HospitalIcon, Siren, MapPin, BadgeCheck, CheckCircle2, Stethoscope } from "lucide-react";

const imgSrc = (url) => (url?.startsWith("/api/") ? fileUrl(url) : url);

const EmergencyModal = ({ open, onOpenChange, hospital }) => {
  const [form, setForm] = useState({ name: "", phone: "", ailment: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await createEmergency({ ...form, hospital: hospital?.name || "" });
      setDone(true);
      toast.success("Emergency request sent — our team will respond immediately.");
    } catch (e) {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ name: "", phone: "", ailment: "", message: "" }); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="emergency-dialog" className="max-w-md bg-brand-cream border-border">
        {done ? (
          <div className="py-8 text-center" data-testid="emergency-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-500/15 flex items-center justify-center mb-5">
              <span className="relative flex h-4 w-4">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500" />
              </span>
            </div>
            <h3 className="font-serif text-2xl text-brand-ink mb-2">Help is on the way</h3>
            <p className="text-brand-ink/70 text-sm mb-6">
              Thank you, {form.name.split(" ")[0]}. Our team will call you back immediately to coordinate urgent help
              {hospital ? ` near ${hospital.name}` : ""}.
            </p>
            <Button onClick={() => close(false)} className="rounded-full bg-brand hover:bg-brand-hover text-white px-8">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-light text-brand-ink flex items-center gap-2">
                <Siren className="text-red-500" size={22} /> Request emergency help now
              </DialogTitle>
              <DialogDescription className="text-brand-ink/60">
                {hospital ? `For ${hospital.name}, ${hospital.area}. ` : ""}Leave your details — our team responds immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-brand-ink/80 text-sm mb-1.5 block">Your name</Label>
                <Input data-testid="emergency-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <Label className="text-brand-ink/80 text-sm mb-1.5 block">Phone number</Label>
                <Input data-testid="emergency-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." />
              </div>
              <div>
                <Label className="text-brand-ink/80 text-sm mb-1.5 block">What's the problem / ailment?</Label>
                <Input data-testid="emergency-ailment" value={form.ailment} onChange={(e) => set("ailment", e.target.value)} placeholder="e.g. chest pain, fall, high BP" />
              </div>
              <div>
                <Label className="text-brand-ink/80 text-sm mb-1.5 block">Anything else? (optional)</Label>
                <Textarea data-testid="emergency-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={2} placeholder="Location details, condition..." />
              </div>
              <Button data-testid="emergency-submit" onClick={submit} disabled={loading} className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white h-12">
                {loading ? "Sending..." : "Send emergency request"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Hospitals = ({ onBook }) => {
  const [hospitals, setHospitals] = useState([]);
  const [emOpen, setEmOpen] = useState(false);
  const [emHospital, setEmHospital] = useState(null);

  useEffect(() => {
    listHospitals().then(setHospitals).catch(() => {});
  }, []);

  const openEmergency = (h) => {
    setEmHospital(h);
    setEmOpen(true);
  };

  return (
    <section id="hospitals" data-testid="hospitals" className="py-24 lg:py-32 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-5">
              Partner hospitals
            </p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1] tracking-tight">
              Quick response, matched to the
              <span className="italic text-brand"> ailment</span>.
            </h2>
            <p className="mt-5 text-brand-ink/70 leading-relaxed">
              Trusted hospitals across Jaipur. In an emergency, request help and we'll coordinate transport,
              accompaniment and the right department — fast.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {hospitals.map((h, i) => (
            <Reveal key={h.id} delay={(i % 3) * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.4 }}
                data-testid={`hospital-card-${h.id}`}
                className="group h-full rounded-3xl overflow-hidden bg-white border border-border flex flex-col"
              >
                <div className="relative bg-brand-ink px-6 pt-6 pb-5">
                  <div className="grain opacity-20" />
                  <div className="relative flex items-start justify-between">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur">
                      <HospitalIcon className="text-brand-gold" size={24} />
                    </span>
                    {h.emergency_24x7 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-xs font-semibold px-2.5 py-1">
                        <Siren size={12} /> 24×7 Emergency
                      </span>
                    )}
                  </div>
                  <h3 className="relative font-serif text-2xl text-brand-cream leading-tight mt-4">{h.name}</h3>
                  {h.area && (
                    <p className="relative mt-1 text-sm text-brand-cream/70 flex items-center gap-1.5">
                      <MapPin size={14} className="text-brand-gold" /> {h.area}
                    </p>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  {h.ailments?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {h.ailments.map((a) => (
                        <span key={a} className="inline-flex items-center gap-1 rounded-full bg-brand-sand text-brand-ink/80 text-xs px-2.5 py-1">
                          <Stethoscope size={11} className="text-brand" /> {a}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto pt-6 flex flex-col gap-2">
                    <Button
                      data-testid={`hospital-accompany-${h.id}`}
                      onClick={onBook}
                      variant="outline"
                      className="w-full rounded-full border-brand text-brand hover:bg-brand/5"
                    >
                      Request accompaniment
                    </Button>
                    <Button
                      data-testid={`hospital-emergency-${h.id}`}
                      onClick={() => openEmergency(h)}
                      className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Siren size={15} /> Emergency help now
                    </Button>
                  </div>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {hospitals.length === 0 && (
          <Reveal>
            <div className="mt-12 rounded-3xl bg-white border border-border p-8 sm:p-12" data-testid="hospitals-empty">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-ink shrink-0">
                  <HospitalIcon className="text-brand-gold" size={28} />
                </span>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-brand-ink leading-tight">
                    Our affiliated hospital network is on its way
                  </h3>
                  <p className="mt-3 text-brand-ink/70 leading-relaxed max-w-2xl">
                    We're carefully onboarding trusted hospitals across Jaipur. Once live, you'll be able to
                    request an <b>accompaniment</b> or <b>emergency help</b> matched to your parents' ailment —
                    with transport, queue handling and the right department arranged for you. In the meantime,
                    if you need urgent help, reach out now and our team will respond immediately.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button
                      data-testid="empty-emergency-btn"
                      onClick={() => openEmergency(null)}
                      className="rounded-full bg-red-600 hover:bg-red-700 text-white px-6"
                    >
                      <Siren size={15} /> Request emergency help now
                    </Button>
                    <Button
                      data-testid="empty-accompany-btn"
                      onClick={onBook}
                      variant="outline"
                      className="rounded-full border-brand text-brand hover:bg-brand/5 px-6"
                    >
                      Request a hospital visit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </div>

      <EmergencyModal open={emOpen} onOpenChange={setEmOpen} hospital={emHospital} />
    </section>
  );
};
